<script setup>
import { VStructureChooser } from '@qispace/vue3-player'

import { usePlayerStore } from '@/store/players'
import { useProjectStore } from '@/store/project'
import { useBuildingStore } from '@/store/building'
import { useUnitStore } from '@/store/unit'
import { useCardBehaviour } from '@/composables/useCardBehaviour'

import BuildingCard from '@/components/cards/BuildingCard.vue'
import DataSources from '@/components/project/DataSources.vue'

/** Match vue3-player VStructureChooser mobileBreakpoint default */
const MOBILE_BREAKPOINT = 600

const projectStore = useProjectStore()
const buildingStore = useBuildingStore()
const unitStore = useUnitStore()

const playerStore = usePlayerStore()

const buildingStatistics = unitStore.getBuildingUnitsStatistics

const { project: projectSlug } = useRoute().params
const project = projectStore.projectWithFieldsAndStatuses
const buildingChooserData = projectStore.buildingChooserData

const buildingChooser = playerStore.buildingChooser

const { cdnFileResolver, colorCallback, hoverOver, hoverOut, goTo } = buildingChooser

const cfr = (path) => cdnFileResolver(project.assetRoot, path)

const { t: $t } = useI18n()

const version = computed(() => {
    if (project.state !== 'draft') return null
    return (project && project.version) || $t('na')
})

const { $messages } = useNuxtApp()
const { locale } = useI18n()
const i18nConfig = {
    labels: $messages,
    language: locale.value
}

const buildingOnFocus = computed(() => buildingChooser.currentBuildingPublicId)
const buildingOnFocusModel = ref('')

watch(
    buildingOnFocus,
    (newVal) => {
        buildingOnFocusModel.value = newVal
    },
    {
        immediate: true
    }
)

watch(buildingOnFocusModel, (newVal) => {
    buildingChooser.currentBuildingPublicId = newVal
})

const getBuildingSplashImage = buildingStore.getBuildingSplashImage

const WrapperElement = ref(null)
const isMobileSized = ref(false)
const projectCardBehaviour = project.cardBehaviour
const cardCustomPosition = project.cardCustomPosition
const { cardStyle, updateCardPosition, displayCard } = useCardBehaviour(
    WrapperElement,
    projectCardBehaviour,
    cardCustomPosition
)

watch(
    WrapperElement,
    (el, _prev, onCleanup) => {
        if (!el || typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect?.width
            if (typeof width === 'number') {
                isMobileSized.value = width < MOBILE_BREAKPOINT
            }
        })
        observer.observe(el)
        onCleanup(() => observer.disconnect())
    },
    { flush: 'post' }
)

function filterBuildingChooserCameras(data, allowedCameraNames) {
    if (!data?.cameras || !Array.isArray(allowedCameraNames) || allowedCameraNames.length === 0) {
        return data
    }

    const allowed = new Set(allowedCameraNames)
    const cameras = data.cameras.filter((camera) => {
        if (!camera?.cameraName) return true
        if ('filename' in camera) return true
        return allowed.has(camera.cameraName)
    })

    const filtered = { ...data, cameras }

    if (Array.isArray(data.stillCameraIndices)) {
        filtered.stillCameraIndices = cameras
            .map((camera, index) => (!('filename' in camera) ? index : -1))
            .filter((index) => index >= 0)
    }

    return filtered
}

const structureChooserData = computed(() => {
    const data = projectStore.buildingChooserData
    if (!data) return data

    const visibility = projectStore.project?.settings?.buildingChooserCameraVisibility
    if (!visibility) return data

    const allowedNames = isMobileSized.value ? visibility.mobile : visibility.desktop
    return filterBuildingChooserCameras(data, allowedNames)
})

const { $setSize } = useNuxtApp()

const errorState = ref(false)
const playerError = ref(null)

const onPlayerError = (error) => {
    errorState.value = true

    const config = useRuntimeConfig()
    if (config.public.devMode) {
        console.error(error)
    }
    console.log('Hiding the player because of error.')

    playerError.value = error
    $setSize()
}

const activeBuildingIds = buildingStore.buildings.map((b) => b.publicId)

const goToUrl = (url) => {
    navigateWithHash(url)
}

defineExpose({
    playerError,
    errorState
})
</script>
<template>
    <div
        v-if="
            (project.buildingPickerViewMode === 'interactiveImageWithSun' && buildingChooserData) ||
            !errorState
        "
        ref="WrapperElement"
        class="building-chooser-wrapper position-relative"
    >
        <ClientOnly>
            <VStructureChooser
                :structure-chooser-data="structureChooserData"
                :cdn-file-resolver="cfr"
                :i18n-config="i18nConfig"
                :color-callback="colorCallback"
                :selected-id="buildingChooser.currentBuildingPublicId"
                :custom-overlay-content="project.buildingChooserCustomOverlay"
                :version="version"
                :active-structure-ids="activeBuildingIds"
                @on-hover-over="hoverOver"
                @on-hover-out="hoverOut"
                @on-click="(e) => goTo(e, project.slug)"
                @on-mouse-move="(e, pos) => updateCardPosition(e, pos)"
                @on-initialize="$setSize()"
                @on-error="onPlayerError"
            >
            </VStructureChooser>
        </ClientOnly>

        <BuildingCard
            v-if="buildingChooser.hoverBuilding && buildingChooserData && displayCard"
            class="building-chooser-card d-none d-md-block elevation-5"
            :building="buildingChooser.hoverBuilding"
            :project="project"
            :buildingThumbnail="
                getBuildingSplashImage(
                    project.slug,
                    buildingChooser.hoverBuilding ? buildingChooser.hoverBuilding.slug : null
                )
            "
            :buildingStatistics="buildingStatistics"
            :style="cardStyle"
            :minimum-width="200"
        >
        </BuildingCard>

        <DataSources
            v-if="project.settings?.dataSources"
            :data-sources="project.settings?.dataSources"
        />
    </div>

    <!-- Carousel visible on mobile -->
    <div
        v-if="project.buildingPickerViewMode === 'interactiveImageWithSun' && !errorState"
        class="d-flex d-md-none justify-center building-chooser-carousel"
    >
        <v-slide-group
            v-model="buildingOnFocusModel"
            center-active
            show-arrows
            mandatory
            :mobile="true"
            mobile-breakpoint="900"
        >
            <v-slide-group-item
                v-slot="{ select, selectedClass }"
                v-for="(building, index) in buildingStore.buildings"
                :key="index"
                :value="building.publicId"
            >
                <div class="building-chooser-carousel-single-item ma-2">
                    <BuildingCard
                        v-if="building"
                        :class="['ma-1', selectedClass, 'elevation-3']"
                        :building="building"
                        :project="project"
                        :buildingThumbnail="getBuildingSplashImage(project.slug, building.slug)"
                        :buildingStatistics="buildingStatistics"
                        @click="select"
                    >
                        <div class="d-flex justify-center mt-2">
                            <v-btn
                                rounded="xl"
                                class="bg-green text-capitalize px-6"
                                @click="goToUrl(`/${projectSlug}/${building.slug}`)"
                            >
                                {{ $t('viewUnits') }}
                            </v-btn>
                        </div>
                    </BuildingCard>
                </div>
            </v-slide-group-item>
        </v-slide-group>
    </div>

    <v-container
        v-if="project.buildingPickerViewMode === 'cards' || errorState || !buildingChooserData"
        class="dynamic-max-width"
    >
        <div class="d-flex justify-space-around w-20 flex-wrap">
            <div
                v-for="(building, index) in buildingStore.buildings"
                :key="index"
                :value="building.publicId"
            >
                <div class="building-chooser-card-mode ma-2">
                    <BuildingCard
                        v-if="building"
                        :class="['ma-1', 'elevation-3', 'building-chooser-carousel-card']"
                        :building="building"
                        :project="project"
                        :buildingThumbnail="getBuildingSplashImage(project.slug, building.slug)"
                        :buildingStatistics="buildingStatistics"
                        @click="goToUrl(`/${projectSlug}/${building.slug}`)"
                    >
                        <div class="d-flex justify-center mt-2">
                            <v-btn
                                rounded="xl"
                                class="bg-green text-capitalize px-6"
                                link
                                @click="goToUrl(`/${projectSlug}/${building.slug}`)"
                            >
                                {{ $t('viewUnits') }}
                            </v-btn>
                        </div>
                    </BuildingCard>
                </div>
            </div>
        </div>
    </v-container>
</template>
<style scoped>
.building-chooser-wrapper {
    max-height: 50vh;
    margin: 0 auto;
}

.building-chooser-card {
    border: 1.5px solid #fff;
    max-width: 250px;
    min-width: 120px;
}

.v-slide-group-item--active {
    border: 1.5px solid rgb(13, 165, 78, 0.4);
}

.building-chooser-carousel-single-item {
    width: 180px;
}

/* For screen larger than 960 */
@media screen and (min-width: 960px) {
    .building-chooser-wrapper {
        max-height: 85vh;
        aspect-ratio: 16/9;
    }
}

.building-chooser-carousel {
    margin-top: 25px;
}
</style>
